import { useProfile } from '@hooks/api/useAuth';
import { setCountry, setKycStatus } from '@services/analytics';
import { AccountStatus } from 'mh-client';
import { useEffect, useRef } from 'react';

/**
 * Mirrors mobile's user-property sync from AuthCoordinator: whenever the
 * profile becomes available (or changes), push the relevant fields up to
 * GA4 as user properties.
 *
 * Covered today:
 * - `kyc_status` ← profile.accountStatus (shared AccountStatus enum)
 * - `country` ← profile.country (passed through as-is; web stores ISO)
 *
 * Not yet wired: `signup_cohort`, `is_internal` — the ProfileResponse
 * doesn't carry these today (mobile-only profile fields). Add the
 * mapping here once the server surfaces them on the web profile.
 *
 * Mount once in the post-auth layout (RootLayout).
 */
export function useAnalyticsUserProperties(): void {
    const { data: profile } = useProfile();
    const lastKycStatus = useRef<AccountStatus | null | undefined>(undefined);
    const lastCountry = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (!profile) return;

        const status = profile.accountStatus ?? null;
        if (status !== lastKycStatus.current) {
            lastKycStatus.current = status;
            setKycStatus(status);
        }

        const country = profile.country ?? null;
        if (country && country !== lastCountry.current) {
            lastCountry.current = country;
            setCountry(country);
        }
    }, [profile]);
}
